import { useCallback } from "react"
import { useForms } from "queries/forms"
import { Guest, GuestStatus, GuestSummary } from "sharedTypes"
import { replaceMergeTagsInText } from "utilities/strings"

type Props = {
  eventId: string
}

const useMergeTags = ({ eventId }: Props) => {
  const { data: forms } = useForms(eventId)

  const getLinkByFormId = (formId: string, guestFormUrls?: Record<string, string>) => {
    const form = forms?.data.find(({ externalId }) => externalId === formId)

    if (!form) {
      return undefined
    }

    return guestFormUrls?.[formId] ?? form.url
  }

  const replaceMergeTags = useCallback((
    original: string,
    eventTitle: string,
    eventDate: string,
    selectedGuest: GuestSummary | Guest | null,
  ) => {
    const mergeTagReplacements = [
      { mergeTag: "{{EVENT_NAME}}", value: eventTitle },
      { mergeTag: "{{DATE}}", value: eventDate },
    ]
    const bodyWithEventAndDate = replaceMergeTagsInText(original, mergeTagReplacements)
    const guestFormUrls = selectedGuest && "urls" in selectedGuest ? selectedGuest.urls?.forms : undefined

    const formMergeTagReplacements = extractFormIds(bodyWithEventAndDate)
      .filter((formMergeTag) => getLinkByFormId(formMergeTag.id, guestFormUrls) !== undefined)
      .map((formMergeTag) => ({
        mergeTag: formMergeTag.mergeTag,
        value: getLinkByFormId(formMergeTag.id, guestFormUrls)!,
      }))
    const bodyWithMergeTags = replaceMergeTagsInText(bodyWithEventAndDate, formMergeTagReplacements)

    if (!selectedGuest) {
      return bodyWithMergeTags
    }

    const generateGuestMergeTagReplacements = () => {
      const guestName = `${selectedGuest.personInfo.firstName} ${selectedGuest.personInfo.lastName}`

      let confirmationStatus
      switch (selectedGuest.status) {
        case GuestStatus.Confirmed: {
          confirmationStatus = "Confirmed"
          break
        }
        case GuestStatus.Declined: {
          confirmationStatus = "Declined"
          break
        }
        case GuestStatus.Unconfirmed: {
          confirmationStatus = "Unconfirmed"
          break
        }
        case GuestStatus.Waitlisted: {
          confirmationStatus = "Waitlisted"
          break
        }
        default: {
          break
        }
      }

      const seatingInfo = selectedGuest.seatingAssignment ? `${selectedGuest.seatingAssignment.seatingTableTitle} - ${selectedGuest.seatingAssignment.seat}` : ""
      const tableName = selectedGuest.seatingAssignment ? selectedGuest.seatingAssignment.seatingTableTitle : ""

      const guestUrl = ("urls" in selectedGuest && selectedGuest.urls) ? selectedGuest.urls.page : ""
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&data=${encodeURIComponent(guestUrl)}`

      return [
        { mergeTag: "{{FULL_NAME}}", value: guestName },
        { mergeTag: "{{CONFIRMATION_STATUS}}", value: confirmationStatus },
        { mergeTag: "{{SEATING_INFO}}", value: seatingInfo },
        { mergeTag: "{{TITLE}}", value: selectedGuest.personInfo.companyTitle ?? "" },
        { mergeTag: "{{COMPANY}}", value: selectedGuest.personInfo.companyName ?? "" },
        { mergeTag: "{{EMAIL_ADDRESS}}", value: selectedGuest.personInfo.email ?? "" },
        { mergeTag: "{{LAST_NAME}}", value: selectedGuest.personInfo.lastName ?? "" },
        { mergeTag: "{{FIRST_NAME}}", value: selectedGuest.personInfo.firstName ?? "" },
        { mergeTag: "{{TABLE_NAME}}", value: tableName },
        { mergeTag: "{{CONFIRMATION_URL}}", value: guestUrl },
        { mergeTag: "{{GUEST_QR_PAGE_URL}}", value: `${guestUrl}/qr` },
        { mergeTag: "{{GUEST_QR_CODE_IMAGE}}", value: qrCodeUrl },
      ]
    }

    return replaceMergeTagsInText(bodyWithMergeTags, generateGuestMergeTagReplacements())
  }, [forms])

  const getFormsFromTextBody = (body: string) => {
    const extractedFormIds = extractFormIds(body)

    if (forms) {
      return forms.data.filter(
        ({ externalId }) => extractedFormIds.map(({ id }) => id).includes(externalId),
      )
    }

    return []
  }

  const extractFormIds = (text: string) => {
    if (!text) return []

    const regex = /\{\{FORM_[a-zA-Z0-9]+\}\}/g

    const matches = text.match(regex)

    return matches?.map((match) => {
      const id = match.slice(2, -2).split("_")[1]

      return { mergeTag: match, id }
    }) || []
  }

  return { replaceMergeTags, getFormsFromTextBody }
}

export default useMergeTags
